'use client'

import { useState, createContext, useContext } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Arrow from 'public/icons/arrows/arrow-right.svg'
import Typography from 'src/components/design-system/Typography'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Link from 'next/link'

const AccordionContext = createContext({
  openId: null,
  setOpenId: () => {},
  type: 'block',
  color: 'white',
})
const ItemContext = createContext({ id: null })

export default function Accordion({ children, type = 'block', className = '', color = 'white' }) {
  const [openId, setOpenId] = useState(null)
  const Wrapper = type === 'list' ? 'ul' : 'div'

  return (
    <AccordionContext.Provider value={{ openId, setOpenId, type, color }}>
      <Wrapper className={`w-full flex flex-col gap-y-16 ${className}`}>{children}</Wrapper>
    </AccordionContext.Provider>
  )
}

Accordion.Item = function Item({ id, children, className = '' }) {
  const { type, color } = useContext(AccordionContext)
  const Wrapper = type === 'list' ? 'li' : 'div'

  return (
    <ItemContext.Provider value={{ id }}>
      <Wrapper
        className={`border-b ${color == 'black' ? 'border-b-basic-black' : color == 'blue' ? 'border-b-dark-blue-dark-blue2' : color == 'dark-blue' ? 'border-b-dark-blue-dark-blue1' : color == 'transparent' ? "border-b-transparent relative after:bg-style1 after:content-[''] after:w-full after:h-4 after:block after:absolute after:left-0 after:bottom-0" : 'border-b-basic-white'} pb-8 ${className}`}
      >
        {children}
      </Wrapper>
    </ItemContext.Provider>
  )
}

Accordion.Trigger = function Trigger({
  children,
  variant = 'button',
  href = '#',
  icon = 'arrow',
  linkOnClick = () => null,
}) {
  const { openId, setOpenId, color } = useContext(AccordionContext)
  const { id } = useContext(ItemContext)
  const opened = openId === id
  const toggle = () => setOpenId(opened ? null : id)
  const translate = useTranslateEntry()
  const contentId = `accordion-content-${id}`

  const arrow =
    icon === 'arrow' ? (
      <motion.span
        animate={{ rotate: opened ? -90 : 0 }}
        transition={{ duration: 0.25 }}
        className={`rounded-full border transition-colors ${opened && color != 'dark-blue' ? `border-dark-blue-dark-blue5` : `${color == 'black' ? 'border-basic-black' : color == 'blue' ? 'border-dark-blue-dark-blue2' : color == 'dark-blue' ? 'border-dark-blue-dark-blue1' : 'border-basic-white'}`} w-18 h-18 flex items-center justify-center group ${color == 'dark-blue' ? 'hover:border-dark-blue-dark-blue2' : 'hover:border-dark-blue-dark-blue5'}`}
      >
        <Arrow
          className={`transition-colors ${opened && color != 'dark-blue' ? `text-dark-blue-dark-blue5` : `${color == 'black' ? 'text-basic-black' : color == 'blue' ? 'text-dark-blue-dark-blue2' : color == 'dark-blue' ? 'text-dark-blue-dark-blue1' : 'text-basic-white'}`} ${color == 'dark-blue' ? 'group-hover:text-dark-blue-dark-blue2' : 'group-hover:text-dark-blue-dark-blue5'}`}
        />
      </motion.span>
    ) : icon === 'plus' ? (
      <div
        className={`rounded-full border relative transition-colors ${opened && color != 'dark-blue' ? `border-dark-blue-dark-blue5` : `${color == 'black' ? 'border-basic-black' : color == 'blue' ? 'border-dark-blue-dark-blue2' : color == 'dark-blue' ? 'border-dark-blue-dark-blue1' : 'border-basic-white'}`} w-18 h-18 flex items-center justify-center group ${color == 'dark-blue' ? 'hover:border-dark-blue-dark-blue2' : 'hover:border-dark-blue-dark-blue5'}`}
      >
        <span
          className={`w-10 h-1 block ${opened && color != 'dark-blue' ? `bg-dark-blue-dark-blue5` : `${color == 'black' ? 'bg-basic-black' : color == 'blue' ? 'bg-dark-blue-dark-blue2' : color == 'dark-blue' ? 'bg-dark-blue-dark-blue1' : 'bg-basic-white'}`} absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`}
        ></span>
        <span
          className={`w-1 h-10 block ${color == 'black' ? 'bg-basic-black' : color == 'blue' ? 'bg-dark-blue-dark-blue2' : color == 'dark-blue' ? 'bg-dark-blue-dark-blue1' : 'bg-basic-white'} transition-opacity ${!opened ? `opacity-0` : ''} absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 `}
        ></span>
      </div>
    ) : null

  if (variant === 'button') {
    return (
      <button
        onClick={toggle}
        aria-expanded={opened}
        aria-controls={contentId}
        aria-label={translate('T_EXPAND')}
        className="w-full flex items-center justify-between group"
      >
        <Typography
          variant="h3-caps-bold"
          component="span"
          color={
            opened && color != 'dark-blue'
              ? `text-dark-blue-dark-blue5`
              : `${color == 'black' ? 'text-basic-black' : color == 'blue' ? 'text-dark-blue-dark-blue2' : color == 'dark-blue' ? 'text-dark-blue-dark-blue1' : 'text-basic-white'}`
          }
          className={`${color == 'dark-blue' ? 'group-hover:text-dark-blue-dark-blue2' : 'group-hover:text-dark-blue-dark-blue5'} transition-colors`}
        >
          {children}
        </Typography>
        {arrow}
      </button>
    )
  }

  if (variant === 'link') {
    return (
      <div className="relative w-full">
        <Link
          href={href}
          className={`block relative ${opened && color != 'dark-blue' ? `w-fit after:w-0 after:content-[''] after:block after:h-1 after:bg-dark-blue-dark-blue5 after:transition-[width] after:duration-300 after:ease-out after:absolute after:bottom-0 after:left-0 hover:after:w-full` : 'w-full'} group`}
          onClick={linkOnClick}
        >
          <Typography
            variant="h3-caps-bold"
            component="span"
            color={
              opened && color != 'dark-blue'
                ? `text-dark-blue-dark-blue5`
                : `${color == 'black' ? 'text-basic-black' : color == 'blue' ? 'text-dark-blue-dark-blue2' : color == 'dark-blue' ? 'text-dark-blue-dark-blue1' : 'text-basic-white'}`
            }
            className={`transition-colors ${color == 'dark-blue' ? 'group-hover:!text-dark-blue-dark-blue2' : 'group-hover:!text-dark-blue-dark-blue5'}`}
          >
            {children}
          </Typography>
        </Link>
        <button
          onClick={toggle}
          aria-expanded={opened}
          aria-controls={contentId}
          aria-label={translate('T_EXPAND')}
          className="absolute top-1/2 -translate-y-1/2 right-0 w-18 h-18 z-[2] cursor-pointer"
        >
          {arrow}
        </button>
      </div>
    )
  }

  if (variant === 'only-link') {
    return (
      <Link
        href={href}
        className={`w-full flex items-center justify-between group relative after:content-[''] after:block after:absolute after:top-[calc(100%_+_0.5rem)] after:left-0 after:w-full after:h-3 after:origin-top after:scale-y-0 hover:after:scale-y-100 after:transition-transform after:duration-300 after:ease-out ${color == 'black' || color == 'blue' || color == 'dark-blue' ? 'after:bg-dark-blue-dark-blue3' : 'after:bg-dark-blue-dark-blue5'}`}
        onClick={linkOnClick}
      >
        <Typography
          variant="h3-caps-bold"
          component="span"
          color={`${color == 'black' ? 'text-basic-black' : color == 'blue' ? 'text-dark-blue-dark-blue2' : color == 'dark-blue' ? 'text-dark-blue-dark-blue1' : 'text-basic-white'}`}
          className={`${color == 'black' || color == 'blue' || color == 'dark-blue' ? 'group-hover:text-dark-blue-dark-blue3' : 'group-hover:text-dark-blue-dark-blue5'} transition-colors`}
        >
          {children}
        </Typography>
        <span
          className={`rounded-full border transition-colors ${color == 'black' ? 'text-basic-black' : color == 'blue' ? 'text-dark-blue-dark-blue2' : color == 'dark-blue' ? 'text-dark-blue-dark-blue1' : 'text-basic-white'} w-18 h-18 flex items-center justify-center 
          ${color == 'black' || color == 'blue' || color == 'dark-blue' ? 'group-hover:border-dark-blue-dark-blue3' : 'group-hover:border-dark-blue-dark-blue5'}`}
        >
          <Arrow
            className={`transition-colors ${color == 'black' || color == 'blue' || color == 'dark-blue' ? 'group-hover:text-dark-blue-dark-blue3' : 'group-hover:text-dark-blue-dark-blue5'}`}
          />
        </span>
      </Link>
    )
  }

  return null
}

Accordion.Content = function Content({ children }) {
  const { openId } = useContext(AccordionContext)
  const { id } = useContext(ItemContext)
  const opened = openId === id
  const contentId = `accordion-content-${id}`

  return (
    <AnimatePresence initial={false}>
      {opened && (
        <motion.div
          id={contentId}
          role="region"
          aria-labelledby={`accordion-trigger-${id}`}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
